import React, { useState } from "react";
import { Box, Button, Divider, Radio, Typography } from "@mui/material";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { Account } from "@rentablo/api/apiv1";
import { LoadingButton } from "@rentablo/components";
import { GroupedAccountList } from "@features/accounts/edit/components/AdditionalContraAccounts/GroupedAccountList";

const Panel = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.border.secondary}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  marginTop: theme.spacing(3),
}));

const ScrollableList = styled("div")({
  maxHeight: 360,
  overflowY: "auto",
});

interface Props {
  accounts: Account[];
  currentPrimaryId?: number;
  onConfirm: (id: number) => Promise<void>;
  onCancel: () => void;
  isFetching: boolean;
}

export const SelectPrimaryPanel = ({
  accounts,
  currentPrimaryId,
  onConfirm,
  onCancel,
  isFetching,
}: Props) => {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState<number | undefined>(
    currentPrimaryId
  );

  const items = accounts.map((a) => ({
    id: a.id,
    bankName: a.bankName,
    accountName: a.name,
    iban: a.iban,
    bankImage: a.iconUrl,
    type: a.typeFromBank,
    accountType: a.type,
  }));

  const isUnchanged = selectedId === currentPrimaryId;

  return (
    <Panel data-testid="select-primary-panel">
      <Typography variant="body4" fontWeight={600} mb={3} display="block">
        {t("referenceAccountPage.selectPrimary.title")}
      </Typography>

      <ScrollableList>
        <GroupedAccountList
          items={items}
          isSelected={(id) => selectedId === id}
          renderControl={(id, selected) => (
            <Radio
              checked={selected}
              onChange={() => setSelectedId(id)}
              size="small"
              sx={{ p: 0, mr: 4, ml: 0.5, flexShrink: 0 }}
              data-testid={`radio-primary-${id}`}
            />
          )}
        />
      </ScrollableList>

      <Divider sx={{ my: 3 }} />
      <Box display="flex" gap={2}>
        <Button
          variant="tertiary"
          onClick={onCancel}
          data-testid="cancel-select-primary"
          sx={{
            minWidth: 240,
          }}
        >
          {t("referenceAccountPage.cancelButton")}
        </Button>
        <LoadingButton
          variant="primary"
          disabled={isUnchanged || !selectedId}
          isFetching={isFetching}
          onClick={() => selectedId !== undefined && onConfirm(selectedId)}
          sx={{ flex: 2 }}
          data-testid="confirm-select-primary"
        >
          {t("referenceAccountPage.selectPrimary.confirm")}
        </LoadingButton>
      </Box>
    </Panel>
  );
};
